import { ContentCluster } from '@app/components/base/content-structure/ContentCluster';
import { EnrichedVideoEditorEditListItemDto } from '@app/modules/fragments/fragment.types';
import { SOCIAL_EXPORT_FRAGMENT_SOURCE } from '@app/modules/social-export/lib/social-export-fragment-source';
import {
  EditCardRenderStatus,
  isVideoEditorEditRenderCompleted,
  isVideoEditorEditSocialPublishable,
} from '@app/modules/video-editions';
import { LocaleRelativeTime } from '@mi-front/ui';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import ViewTimelineOutlinedIcon from '@mui/icons-material/ViewTimelineOutlined';
import {
  Box,
  Chip,
  FormControl,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { publishMessages } from '../publish-messages';

export interface MediaSelectorProps {
  /** Current selection: fragment source key or a video-editor edit id */
  value: string;
  onChange: (value: string) => void;
  edits: EnrichedVideoEditorEditListItemDto[];
  /** When false, only rendered edits are listed (composition social export). */
  includeFragmentOption?: boolean;
  /**
   * When true, also list edits with a render in progress (pending or completed).
   * Fragment wizard only; composition keeps completed-only listing.
   */
  includeInProgressRenders?: boolean;
}

interface OptionRowContent {
  icon: ReactNode;
  primary: ReactNode;
  secondary: ReactNode;
}

function renderOptionRow(content: OptionRowContent, compact: boolean): ReactNode {
  const inner = (
    <>
      <ListItemIcon sx={compact ? { minWidth: 36 } : undefined}>{content.icon}</ListItemIcon>
      <ListItemText
        primary={content.primary}
        secondary={content.secondary}
        sx={compact ? { my: 0 } : undefined}
      />
    </>
  );
  return compact ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{inner}</Box>
  ) : (
    inner
  );
}

/**
 * Select publishable media: optional original fragment media plus video-editor renders.
 * By default only completed renders; with `includeInProgressRenders`, pending renders too.
 */
export function MediaSelector({
  value,
  onChange,
  edits,
  includeFragmentOption = true,
  includeInProgressRenders = false,
}: MediaSelectorProps) {
  const { $t } = useIntl();

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  const fragmentContent: OptionRowContent = {
    icon: <VideoFileIcon fontSize="small" />,
    primary: $t(publishMessages.mediaSelectorOriginalMedia),
    secondary: '',
  };

  const getEditContent = (edit: EnrichedVideoEditorEditListItemDto): OptionRowContent => {
    const isCompleted = isVideoEditorEditRenderCompleted(edit);
    return {
      icon: <ViewTimelineOutlinedIcon fontSize="small" />,
      primary: (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            my: 0.5,
          }}
        >
          {edit.name}
          <Chip
            size="small"
            variant="outlined"
            label={$t(publishMessages.mediaSelectorEdition)}
            sx={includeFragmentOption ? { fontSize: '11px' } : undefined}
          />
        </Box>
      ),
      secondary:
        isCompleted && edit.renderJob?.finishedOn ? (
          <>
            {$t({
              id: 'videoEditions.finishedOn',
              defaultMessage: 'Generado',
            })}{' '}
            <LocaleRelativeTime value={edit.renderJob.finishedOn} />
          </>
        ) : includeInProgressRenders && edit.renderJob ? (
          <EditCardRenderStatus renderJob={edit.renderJob} />
        ) : (
          <LocaleRelativeTime value={edit.updatedAt} />
        ),
    };
  };

  const selectableEdits = includeInProgressRenders
    ? edits.filter(isVideoEditorEditSocialPublishable)
    : edits.filter(isVideoEditorEditRenderCompleted);

  return (
    <ContentCluster message={publishMessages.mediaSelectorLabel}>
      <FormControl fullWidth sx={{ minWidth: 280 }}>
        <Select
          value={value}
          onChange={handleChange}
          displayEmpty
          renderValue={(v) => {
            if (includeFragmentOption && v === SOCIAL_EXPORT_FRAGMENT_SOURCE) {
              return renderOptionRow(fragmentContent, true);
            }
            const selectedEdit = selectableEdits.find((edit) => edit.id === v);
            if (!selectedEdit) return v;
            return renderOptionRow(getEditContent(selectedEdit), true);
          }}
        >
          {includeFragmentOption && (
            <MenuItem value={SOCIAL_EXPORT_FRAGMENT_SOURCE} sx={{ minHeight: '56px !important' }}>
              {renderOptionRow(fragmentContent, false)}
            </MenuItem>
          )}
          {selectableEdits.map((edit) => (
            <MenuItem key={edit.id} value={edit.id} sx={{ minHeight: 56 }}>
              {renderOptionRow(getEditContent(edit), false)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </ContentCluster>
  );
}
